import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

const STORAGE_KEY = 'pv_recently_viewed';
const MAX_STORED = 20;
// Ads subdomain: cards link to the PDP instead of adding to cart.
const IS_ADS_DOMAIN =
  location.hostname === 'ads.powdervalley.com' || location.hostname === 'ads-qa.powdervalley.com';
const INFO_ICON = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 2a8 8 0 100 16A8 8 0 0010 2zm0 14a6 6 0 110-12 6 6 0 010 12zm1-9H9v2H7v2h2v2h2v-2h2V9h-2V7z" fill="currentColor"/></svg>';
const CART_ICON = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M6.67 17.5a.83.83 0 100-1.67.83.83 0 000 1.67zM15 17.5a.83.83 0 100-1.67.83.83 0 000 1.67zM.83.83h3.34l2.23 11.16a1.67 1.67 0 001.67 1.34h8.1a1.67 1.67 0 001.66-1.34L19.17 5H5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const BELL_ICON = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 8.33a5 5 0 1110 0c0 5.84 2.5 7.5 2.5 7.5h-15S5 14.17 5 8.33z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.44 19.17a1.67 1.67 0 01-2.88 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function pvParsePositive(value, fallback) {
  const n = parseInt(value, 10);
  return n >= 1 ? n : fallback;
}

function pvClampQty(input, candidate) {
  const min = pvParsePositive(input.min, 1);
  const step = pvParsePositive(input.step, 1);
  const max = parseInt(input.max, 10);
  let n = parseInt(candidate, 10);
  if (isNaN(n) || n < min) n = min;
  if (max >= min && n > max) n = max;
  if (step > 1) {
    const offset = (n - min) % step;
    if (offset !== 0) n = n - offset;
  }
  input.value = n;
  return n;
}

function pvStopOnQtyInput(e) {
  if (e.target?.matches?.('input[class$="__qty-value"]')) e.stopPropagation();
}

// One combined fetch per product (templates/product.card-data.liquid) returns
// the product fields, qty-config, badge HTML and MAP/shipping flags in a single
// JSON blob — replaces the old separate .js + ?view=qty-config + ?view=badge
// requests. Cached per handle so the carousel doesn't refetch on re-render.
// Falls back to the reliable .js + view snippets if the combined view isn't on
// the theme (e.g. not yet deployed) so cards never render blank.
const pvCardDataCache = {};
let pvCardDataUnavailable = false;

function fetchCardData(handle) {
  if (pvCardDataCache[handle] !== undefined) return pvCardDataCache[handle];
  if (pvCardDataUnavailable) {
    pvCardDataCache[handle] = fetchCardDataLegacy(handle);
    return pvCardDataCache[handle];
  }
  // B2B: card-data prices are contextual (company catalog); the loc param is
  // only a browser cache-key so a company-location switch never reuses a
  // stale-priced response. Renders as no param for guests.
  const pvLocKey = window.__pvB2BLocationId ? `&loc=${encodeURIComponent(window.__pvB2BLocationId)}` : '';
  pvCardDataCache[handle] = fetch(`/products/${handle}?view=card-data${pvLocKey}`)
    .then((res) =>
      res.text().then((text) => {
        if (!res.ok) throw new Error(`card-data status ${res.status}`);
        try {
          return JSON.parse(text);
        } catch (e) {
          pvCardDataUnavailable = true; // 200 but HTML → view missing; stop trying
          throw e;
        }
      })
    )
    .catch(() => fetchCardDataLegacy(handle));
  return pvCardDataCache[handle];
}

// Legacy fallback: reliable /products/{handle}.js (always JSON) plus qty-config
// and badge views, shaped to match the combined blob (qty + badge attached).
function fetchCardDataLegacy(handle) {
  return Promise.all([
    fetch(`/products/${handle}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
    fetch(`/products/${handle}?view=qty-config`).then((r) => (r.ok ? r.text() : '')).catch(() => ''),
    fetch(`/products/${handle}?view=badge`).then((r) => (r.ok ? r.text() : '')).catch(() => ''),
  ]).then(([product, qtyTxt, badgeHtml]) => {
    if (!product) return null;
    const qty = { min: 1, step: 1, max: 0, restricted: false };
    try {
      const p = JSON.parse(qtyTxt);
      if (p) {
        if (p.min >= 1) qty.min = p.min;
        if (p.step >= 1) qty.step = p.step;
        if (p.max >= 1) qty.max = p.max;
        qty.restricted = !!p.restricted;
      }
    } catch {
      /* defaults */
    }
    product.qty = qty;
    product.badge = badgeHtml && badgeHtml.trim() ? badgeHtml : null;
    return product;
  });
}

function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2);
}

// Fallback when a product has no featured_image. Clones the shared
// `<template id="pv-image-placeholder">` rendered by theme.liquid (snippet
// `pv-image-placeholder.liquid`) — same PVO-logo / frosty-background treatment
// used on the PDP and on Liquid-rendered cards.
function pvPlaceholderImage() {
  var tpl = document.getElementById('pv-image-placeholder');
  return tpl ? tpl.innerHTML : '';
}

// Judge.me badge now ships inside the combined card-data blob, so it reads from
// the cached fetchCardData() response — no separate request.
function injectBadges(root) {
  root.querySelectorAll('.pv-recent__stars[data-product-handle]').forEach((slot) => {
    const handle = slot.dataset.productHandle;
    if (!handle || slot.dataset.pvBadgeLoaded === '1') return;
    slot.dataset.pvBadgeLoaded = '1';
    fetchCardData(handle).then((data) => {
      const html = data && data.badge && String(data.badge).trim() ? data.badge : null;
      if (!html) return;
      slot.innerHTML = html;
    });
  });
}

// Storage entries are objects { h: handle, m: 1|0 } where `m` is the MAP-hide flag
// captured when the user visited that product's PDP (PVSM-78). Legacy entries
// stored as plain strings are normalized on read.
function readEntries() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    return raw
      .map((item) => (typeof item === 'string' ? { h: item, m: 0 } : item))
      .filter((item) => item && item.h);
  } catch {
    return [];
  }
}

function writeEntries(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage full or unavailable
  }
}

function recordProduct(handle, mapEnabled) {
  if (!handle) return;
  const entries = readEntries().filter((e) => e.h !== handle);
  entries.unshift({ h: handle, m: mapEnabled ? 1 : 0 });
  writeEntries(entries.slice(0, MAX_STORED));
}

function buildLabelsHTML(product) {
  const tags = product.tags || [];
  const onSale = product.compare_at_price && product.compare_at_price > product.price;
  const hasNew = tags.includes('new');
  const hasComingSoon = tags.includes('coming-soon');

  const labels = [];
  if (onSale) labels.push('<span class="pv-label pv-label--sale">Sale</span>');
  if (hasNew) labels.push('<span class="pv-label pv-label--new">New</span>');
  if (hasComingSoon) labels.push('<span class="pv-label pv-label--coming-soon">Coming Soon</span>');

  if (labels.length === 0) return '';
  return `<div class="pv-labels">${labels.join('')}</div>`;
}

function buildPriceHTML(product, prefix, mapEnabled) {
  // PV: $0 variants are non-purchasable (#96) — exclude them so cards never
  // show "$0.00 –". Range reflects every priced variant; stock shows separately.
  const priced = (product.variants || []).filter((v) => v.price > 0);

  let rangeMin = Infinity;
  let rangeMax = 0;
  let compareMin = Infinity;
  let compareMax = 0;
  let hasCompare = false;

  for (const v of priced) {
    if (v.price < rangeMin) rangeMin = v.price;
    if (v.price > rangeMax) rangeMax = v.price;
    const original = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : v.price;
    if (v.compare_at_price && v.compare_at_price > v.price) hasCompare = true;
    if (original < compareMin) compareMin = original;
    if (original > compareMax) compareMax = original;
  }

  const pricesVary = priced.length > 1 && rangeMin !== rangeMax;
  const compareDiverges = compareMin < rangeMin || compareMax > rangeMax;
  const onSale = hasCompare;

  // PVSM-78: when MAP is enabled AND product is on sale, hide all pricing and
  // link to the PDP. Add-to-cart flow on the card still works; price reveals
  // in the cart drawer after adding.
  if (mapEnabled && onSale) {
    const url = `/products/${product.handle}`;
    return {
      rangeClass: '',
      html: `<a class="pv-hidden-price" href="${url}">Add to cart to see price</a>`,
    };
  }

  if (pricesVary) {
    const range = `${formatMoney(rangeMin)} – ${formatMoney(rangeMax)}`;
    if (hasCompare && compareDiverges) {
      const compareRange = `${formatMoney(compareMin)} – ${formatMoney(compareMax)}`;
      return {
        rangeClass: `${prefix}__card-price--range`,
        html: `<span class="${prefix}__price ${prefix}__price--sale">${range}</span>
       <span class="${prefix}__price ${prefix}__price--compare">${compareRange}</span>`,
      };
    }
    return {
      rangeClass: `${prefix}__card-price--range`,
      html: `<span class="${prefix}__price">${range}</span>`,
    };
  }

  if (priced.length === 0) {
    // Every variant is $0 — non-purchasable (#96); mirror product-level fallback.
    const zeroHtml = product.compare_at_price && product.compare_at_price > product.price
      ? `<span class="${prefix}__price ${prefix}__price--sale">${formatMoney(product.price)}</span>
       <span class="${prefix}__price ${prefix}__price--compare">${formatMoney(product.compare_at_price)}</span>`
      : `<span class="${prefix}__price">${formatMoney(product.price)}</span>`;
    return { rangeClass: '', html: zeroHtml };
  }

  const html = hasCompare && compareDiverges
    ? `<span class="${prefix}__price ${prefix}__price--sale">${formatMoney(rangeMin)}</span>
       <span class="${prefix}__price ${prefix}__price--compare">${formatMoney(compareMax)}</span>`
    : `<span class="${prefix}__price">${formatMoney(rangeMin)}</span>`;
  return { rangeClass: '', html };
}

function buildCardHTML(product, prefix, mapEnabled, qtyConfig) {
  const url = `/products/${product.handle}`;
  const available = product.available;
  const firstVariant = product.variants?.[0];
  const multiVariant = product.variants?.length > 1;
  const image = product.featured_image
    ? `<img class="${prefix}__card-image" src="${product.featured_image}" loading="lazy" alt="${product.title}">`
    : pvPlaceholderImage();
  const labels = buildLabelsHTML(product);
  const stockClass = available ? `${prefix}__stock--in` : `${prefix}__stock--out`;
  const stockText = available ? 'In stock' : 'Out of stock';
  const price = buildPriceHTML(product, prefix, mapEnabled);

  const klaviyoEnabled = typeof window.__pvIsKlaviyoEnabled === 'function' ? window.__pvIsKlaviyoEnabled() : false;
  // $0 variants are non-purchasable (#96). Treat a product as purchasable when
  // ANY variant is priced (product.price is price_min, which a $0 variant zeroes).
  const hasPrice = (product.variants || []).some((v) => typeof v.price === 'number' && v.price > 0);

  let atcHTML = '';
  if (!hasPrice) {
    atcHTML = '';
  } else if (!available) {
    if (klaviyoEnabled) {
      atcHTML = `<button type="button" class="${prefix}__atc-btn ${prefix}__atc-btn--notify" data-bis-variant-id="${firstVariant?.id}" data-bis-product-id="${product.id}" data-bis-product-handle="${product.handle}">${BELL_ICON} NOTIFY ME</button>`;
    } else {
      atcHTML = `<button type="button" class="${prefix}__atc-btn ${prefix}__atc-btn--soldout" disabled>SOLD OUT</button>`;
    }
  } else if (IS_ADS_DOMAIN) {
    atcHTML = `<a href="${url}" class="${prefix}__atc-btn">${INFO_ICON} VIEW DETAILS</a>`;
  } else if (multiVariant) {
    atcHTML = `<a href="${url}" class="${prefix}__atc-btn">${CART_ICON} SELECT OPTIONS</a>`;
  } else {
    const restrictionAttr = qtyConfig && qtyConfig.restricted ? ' data-pv-min-max-restriction="true"' : '';
    atcHTML = `<button class="${prefix}__atc-btn" data-add-to-cart="${firstVariant.id}"${restrictionAttr}>${CART_ICON} ADD TO CART</button>`;
  }

  const cfg = qtyConfig || { min: 1, step: 1, max: 0 };
  const maxAttr = cfg.max > 0 ? ` max="${cfg.max}"` : '';
  const qtyRowHTML = !multiVariant && available && hasPrice && !IS_ADS_DOMAIN
    ? `<div class="${prefix}__qty-row">
          <span class="${prefix}__qty-label">Quantity:</span>
          <div class="${prefix}__qty">
            <button class="${prefix}__qty-btn" type="button" data-qty-action="minus" aria-label="Decrease quantity">&minus;</button>
            <input class="${prefix}__qty-value" type="number" inputmode="numeric" pattern="[0-9]*" min="${cfg.min}"${maxAttr} step="${cfg.step}" value="${cfg.min}" aria-label="Quantity">
            <button class="${prefix}__qty-btn" type="button" data-qty-action="plus" aria-label="Increase quantity">+</button>
          </div>
        </div>`
    : '';

  const escapedTitle = (product.title || '').replace(/"/g, '&quot;');
  return `
    <div class="${prefix}__card">
      <div class="${prefix}__card-image-wrap">
        <a href="${url}" class="${prefix}__card-image-link" aria-label="${escapedTitle}">
          ${image}
        </a>
        ${labels}
      </div>
      <div class="${prefix}__card-info">
        <a href="${url}" class="${prefix}__card-title">${product.title}</a>
        <div class="${prefix}__card-meta">
          <span class="${prefix}__stock ${stockClass}">
            <span class="${prefix}__stock-dot"></span>
            ${stockText}
          </span>
          <div class="${prefix}__stars" data-product-handle="${product.handle}"></div>
        </div>
        <div class="${prefix}__card-price ${price.rangeClass}">${price.html}</div>
        ${qtyRowHTML}
      </div>
      ${atcHTML}
    </div>`;
}

class PvRecentlyViewed extends Component {
  requiredRefs = ['productsTrack'];

  #prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  #onTrackScroll = () => {
    this.#updateProgress();
    this.#updateNavState();
  };

  connectedCallback() {
    super.connectedCallback();

    const { productsTrack, prevBtn, nextBtn } = this.refs;

    // Record current product (PVSM-78: stamp MAP flag so subsequent views can hide price)
    recordProduct(this.dataset.productHandle, this.dataset.productMapEnabled === '1');

    // Carousel navigation
    if (prevBtn) prevBtn.addEventListener('click', () => this.#scroll(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => this.#scroll(1));

    if (productsTrack) {
      productsTrack.addEventListener('scroll', this.#onTrackScroll, { passive: true });
    }

    // Quantity selectors — +/- buttons and direct input editing.
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-action]');
      if (!btn) return;

      const wrapper = btn.closest('.pv-recent__qty');
      const input = wrapper?.querySelector('.pv-recent__qty-value');
      if (!input) return;

      const step = pvParsePositive(input.step, 1);
      const current = parseInt(input.value, 10) || pvParsePositive(input.min, 1);
      const next = btn.dataset.qtyAction === 'minus' ? current - step : current + step;
      pvClampQty(input, next);
    });

    this.addEventListener('mousedown', pvStopOnQtyInput);
    this.addEventListener('pointerdown', pvStopOnQtyInput);
    this.addEventListener('click', pvStopOnQtyInput);
    this.addEventListener('focusin', pvStopOnQtyInput);
    this.addEventListener('keydown', (e) => {
      if (!e.target.classList?.contains('pv-recent__qty-value')) return;
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        e.target.blur();
      }
    });
    this.addEventListener('input', (e) => {
      if (!e.target.classList?.contains('pv-recent__qty-value')) return;
      e.stopPropagation();
      const max = parseInt(e.target.max, 10);
      const raw = parseInt(e.target.value, 10);
      if (!isNaN(raw) && max && raw > max) e.target.value = max;
    });
    this.addEventListener('change', (e) => {
      if (!e.target.classList?.contains('pv-recent__qty-value')) return;
      pvClampQty(e.target, parseInt(e.target.value, 10));
    });
    this.addEventListener('blur', (e) => {
      if (!e.target.classList?.contains('pv-recent__qty-value')) return;
      pvClampQty(e.target, parseInt(e.target.value, 10));
    }, true);

    // Add to cart (delegated)
    this.addEventListener('click', (e) => {
      const cartBtn = e.target.closest('[data-add-to-cart]');
      if (!cartBtn) return;
      e.preventDefault();

      const variantId = cartBtn.dataset.addToCart;
      const card = cartBtn.closest('.pv-recent__card');
      const qtyEl = card?.querySelector('.pv-recent__qty-value');
      const quantity = parseInt(qtyEl?.value, 10) || 1;

      if (!cartBtn._originalHtml) {
        cartBtn._originalHtml = cartBtn.innerHTML;
      }

      const item = { id: parseInt(variantId, 10), quantity };
      if (cartBtn.dataset.pvMinMaxRestriction === 'true') {
        item.properties = { _min_or_max_restriction: 'true' };
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] }),
      })
        .then((res) => res.json())
        .then(() => {
          cartBtn.textContent = 'Added!';
          setTimeout(() => {
            cartBtn.innerHTML = cartBtn._originalHtml;
          }, 1500);

          return fetch('/cart.js').then((r) => r.json());
        })
        .then((cart) => {
          this.dispatchEvent(
            new CartAddEvent(cart, this.dataset.sectionId, {
              source: 'pv-recently-viewed',
              itemCount: cart.item_count,
            })
          );
        })
        .catch(() => {
          cartBtn.textContent = 'Error';
          setTimeout(() => {
            cartBtn.innerHTML = cartBtn._originalHtml;
          }, 1500);
        });
    });

    // Fetch and render recently viewed products
    this.#fetchAndRender();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { productsTrack } = this.refs;
    if (productsTrack) {
      productsTrack.removeEventListener('scroll', this.#onTrackScroll);
    }
  }

  async #fetchAndRender() {
    const currentHandle = this.dataset.productHandle;
    const maxProducts = parseInt(this.dataset.maxProducts, 10) || 8;
    const entries = readEntries()
      .filter((e) => e.h !== currentHandle)
      .slice(0, maxProducts);

    if (entries.length === 0) return;

    // Show section
    this.style.display = 'block';

    const fetched = await Promise.all(
      entries.map((entry) =>
        fetchCardData(entry.h)
          .then((product) => (product ? { product, mapEnabled: entry.m === 1 } : null))
          .catch(() => null)
      )
    );

    const valid = fetched.filter(
      (item) => item && item.product && !(item.product.tags || []).includes('dont-sync-and-show')
    );
    if (valid.length === 0) {
      this.style.display = 'none';
      return;
    }

    // qty-config (min / step / max) ships inside the combined card-data blob.
    // Falls back to {1,1,0} if it's missing.
    const withQty = valid.map((item) => ({
      ...item,
      qtyConfig: item.product.qty || { min: 1, step: 1, max: 0, restricted: false },
    }));

    const { productsTrack } = this.refs;
    if (!productsTrack) return;

    productsTrack.innerHTML = withQty
      .map(({ product, mapEnabled, qtyConfig }) => buildCardHTML(product, 'pv-recent', mapEnabled, qtyConfig))
      .join('');

    injectBadges(productsTrack);

    // Stagger entrance animations
    const cards = productsTrack.querySelectorAll('.pv-recent__card');
    cards.forEach((card, index) => {
      card.style.setProperty('--pv-recent-card-delay', `${index * 60}ms`);
      card.classList.add('pv-recent__card--enter');
    });

    this.#updateProgress();
    this.#updateNavState();
  }

  #scroll(direction) {
    const { productsTrack } = this.refs;
    if (!productsTrack) return;

    const cardWidth = productsTrack.querySelector('.pv-recent__card')?.offsetWidth || 340;
    const gap = parseFloat(getComputedStyle(productsTrack).gap) || 0;

    productsTrack.scrollBy({
      left: direction * (cardWidth + gap),
      behavior: this.#prefersReduced ? 'auto' : 'smooth',
    });

    setTimeout(() => this.#updateNavState(), 350);
  }

  #updateProgress() {
    const { productsTrack, progressBar } = this.refs;
    if (!productsTrack || !progressBar) return;

    const { scrollLeft, scrollWidth, clientWidth } = productsTrack;
    const maxScroll = scrollWidth - clientWidth;
    const ratio = maxScroll > 0 ? scrollLeft / maxScroll : 0;
    const barWidth = maxScroll > 0 ? (clientWidth / scrollWidth) * 100 : 100;

    progressBar.style.width = `${barWidth}%`;
    progressBar.style.marginLeft = `${ratio * (100 - barWidth)}%`;
  }

  #updateNavState() {
    const { productsTrack, prevBtn, nextBtn } = this.refs;
    if (!productsTrack) return;

    const { scrollLeft, scrollWidth, clientWidth } = productsTrack;
    const maxScroll = scrollWidth - clientWidth;

    prevBtn?.setAttribute('aria-disabled', scrollLeft <= 0 ? 'true' : 'false');
    nextBtn?.setAttribute('aria-disabled', scrollLeft >= maxScroll - 1 ? 'true' : 'false');
  }
}

if (!customElements.get('pv-recently-viewed')) {
  customElements.define('pv-recently-viewed', PvRecentlyViewed);
}
